import type { AppTranslateFn } from "@/hooks/use-app-translation";
import type {
	DynamicDateOption,
	DynamicTimeOption,
} from "../constants/dynamicForm.constant";
import { FIELD_TYPES } from "../constants/dynamicForm.constant";
import type {
	FormField,
	RadioField,
	SelectField,
} from "../interfaces/dynamicForm.interface";
import {
	generateDynamicDate,
	generateDynamicTime,
} from "./dynamicDateGenerator.util";
import { isCheckedValue } from "./is-checked-value.util";
import { storedFileId } from "./stored-file-id.util";
import { storedStringList } from "./stored-string-list.util";

const TIME_OF_DAY_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?$/;

/** Seconds since midnight for `HH:mm` or `HH:mm:ss`. Anything else is not a time. */
export function timeOfDaySeconds(value: string): number | null {
	const match = TIME_OF_DAY_PATTERN.exec(value.trim());
	if (!match) return null;
	return (
		Number(match[1]) * 3600 + Number(match[2]) * 60 + Number(match[3] ?? 0)
	);
}

function resolveTimeBoundSeconds(bound: string): number | null {
	const direct = timeOfDaySeconds(bound);
	if (direct != null) return direct;
	const source = bound.includes("T")
		? bound
		: generateDynamicTime(bound as DynamicTimeOption);
	const date = new Date(source);
	if (Number.isNaN(date.getTime())) return null;
	return date.getHours() * 3600 + date.getMinutes() * 60 + date.getSeconds();
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function createFieldValidator(
	field: FormField,
	t: AppTranslateFn,
	options?: { enforceRequired?: boolean },
) {
	const enforceRequired = options?.enforceRequired !== false;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	return ({ value }: { value: any }) => {
		// Handle required validation for all field types
		if (enforceRequired && field.required) {
			switch (field.type) {
				case "text":
				case "phone":
				case "url":
				case "number":
				case "date":
				case "time":
				case "dateTime":
					if (!value || value === "") {
						return t("common.validation.required");
					}
					break;
				case "checkbox":
				case "switch":
					if (!isCheckedValue(value)) {
						return t("common.validation.required");
					}
					break;
				case "radio":
				case "select":
					if (!value || value === "") {
						return t("common.validation.selectRequired");
					}
					break;
				case "slider":
					if (value === null || value === undefined) {
						return t("common.validation.required");
					}
					break;
				case "toggleButton":
					if (field.exclusive) {
						if (!value || value === "") {
							return t("common.validation.selectRequired");
						}
					} else {
						if (!value || value.length === 0) {
							return t("common.validation.selectAtLeastOne");
						}
					}
					break;
				case "autocomplete":
					if (field.multiple) {
						if (!value || value.length === 0) {
							return t("common.validation.selectAtLeastOne");
						}
					} else {
						if (!value || value === "") {
							return t("common.validation.requiredShort");
						}
					}
					break;
				case "file":
					if (!storedFileId(value)) {
						return t("common.validation.fileRequired");
					}
					break;
				case "multiSelect":
				case "users":
					if (storedStringList(value).length === 0) {
						return t("common.validation.selectAtLeastOne");
					}
					break;
				case "address":
					if (typeof value !== "string" || value.trim() === "") {
						return t("common.validation.required");
					}
					break;
				case "signature":
					if (typeof value !== "string" || value.trim() === "") {
						return t("common.validation.fileRequired");
					}
					break;
			}
		}

		// Handle type-specific validation
		switch (field.type) {
			case FIELD_TYPES.TEXT as "text":
			case FIELD_TYPES.URL as "url":
				// Handle pattern validation for text / URL fields
				if (field.validation?.pattern && value && value !== "") {
					try {
						const regex = new RegExp(field.validation.pattern);
						if (!regex.test(value)) {
							return (
								field.validation.custom ||
								t("common.validation.patternMismatch")
							);
						}
					} catch (error) {
						console.warn("Invalid regex pattern:", field.validation.pattern);
						return t("common.validation.invalidPattern");
					}
				}
				// Handle min/max length validation for text fields
				if (
					field.validation?.minLength &&
					value &&
					value.length < field.validation.minLength
				) {
					return t("common.validation.minLength", {
						count: field.validation.minLength,
					});
				}
				if (
					field.validation?.maxLength &&
					value &&
					value.length > field.validation.maxLength
				) {
					return t("common.validation.maxLength", {
						count: field.validation.maxLength,
					});
				}
				break;
			case FIELD_TYPES.NUMBER as "number":
				if (field.validation?.min && value < field.validation.min) {
					return t("common.validation.minNumber", {
						count: field.validation.min,
					});
				}
				if (field.validation?.max && value > field.validation.max) {
					return t("common.validation.maxNumber", {
						count: field.validation.max,
					});
				}
				break;
			case FIELD_TYPES.TIME_PICKER as "time":
				if (value && value !== "") {
					if (typeof value !== "string") {
						return t("common.validation.dateTimeInvalid");
					}
					const timeSeconds = timeOfDaySeconds(value);
					if (timeSeconds == null) {
						return t("common.validation.dateTimeInvalid");
					}
					if (field.validation?.min) {
						const minSeconds = resolveTimeBoundSeconds(
							String(field.validation.min),
						);
						if (minSeconds != null && timeSeconds < minSeconds) {
							return t("common.validation.dateOnOrAfter", {
								date: String(field.validation.min),
							});
						}
					}
					if (field.validation?.max) {
						const maxSeconds = resolveTimeBoundSeconds(
							String(field.validation.max),
						);
						if (maxSeconds != null && timeSeconds > maxSeconds) {
							return t("common.validation.dateOnOrBefore", {
								date: String(field.validation.max),
							});
						}
					}
				}
				break;
			case FIELD_TYPES.DATE_PICKER as "date":
			case FIELD_TYPES.DATETIME_PICKER as "dateTime":
				if (value && value !== "") {
					// Validate that the value is a valid date
					const date = new Date(value);
					if (isNaN(date.getTime())) {
						return t("common.validation.dateTimeInvalid");
					}

					// Check min date validation
					if (field.validation?.min) {
						let minDate: Date;
						if (
							typeof field.validation.min === "string" &&
							field.validation.min.includes("T")
						) {
							// Static ISO string
							minDate = new Date(field.validation.min);
						} else {
							// Dynamic option
							const minValue = generateDynamicDate(
								field.validation.min as DynamicDateOption,
							);
							minDate = new Date(minValue);
						}
						if (date < minDate) {
							return t("common.validation.dateOnOrAfter", {
								date: minDate.toLocaleDateString(),
							});
						}
					}

					// Check max date validation
					if (field.validation?.max) {
						let maxDate: Date;
						if (
							typeof field.validation.max === "string" &&
							field.validation.max.includes("T")
						) {
							// Static ISO string
							maxDate = new Date(field.validation.max);
						} else {
							// Dynamic option
							const maxValue = generateDynamicDate(
								field.validation.max as DynamicDateOption,
							);
							maxDate = new Date(maxValue);
						}
						if (date > maxDate) {
							return t("common.validation.dateOnOrBefore", {
								date: maxDate.toLocaleDateString(),
							});
						}
					}
				}
				break;
			case FIELD_TYPES.SELECT as "select": {
				const sf = field as SelectField;
				const allowed = sf.options.map((o) => o.value);
				if (sf.multiple && Array.isArray(value)) {
					for (const v of value) {
						if (typeof v === "string" && !allowed.includes(v)) {
							return t("common.validation.invalidSelection");
						}
					}
				} else if (
					typeof value === "string" &&
					value !== "" &&
					!allowed.includes(value)
				) {
					return t("common.validation.invalidSelection");
				}
				if (
					enforceRequired &&
					field.multiple &&
					field.required &&
					(!value || value.length === 0)
				) {
					return t("common.validation.selectAtLeastOne");
				}
				break;
			}
			case FIELD_TYPES.RADIO as "radio": {
				const rf = field as RadioField;
				const allowedRadio = rf.options.map((o) => o.value);
				if (
					typeof value === "string" &&
					value !== "" &&
					!allowedRadio.includes(value)
				) {
					return t("common.validation.invalidSelection");
				}
				break;
			}
			case FIELD_TYPES.SLIDER as "slider":
				if (value < field.min) {
					return t("common.validation.minNumber", { count: field.min });
				}
				if (value > field.max) {
					return t("common.validation.maxNumber", { count: field.max });
				}
				break;
			case FIELD_TYPES.FILE_UPLOAD as "file": {
				if (Array.isArray(value) && value.length > 0) {
					return t("common.validation.fileRequired");
				}
				break;
			}
		}

		// No validation errors
		return undefined;
	};
}
